import { ColorSwatchCategory, hexColorSwatches } from "@nova/constants"
import EventEmitter from "events"
import { customMapController, disabledMapController } from "features/map/control/slice"
import { useMapControl } from "features/map/control/useMapControl"
import store from "integrations/redux/store"
import { throttle } from "nodash"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { useCallback, useLayoutEffect, useRef, useState } from "react"

export const firePerimeterDragController = new EventEmitter()
const draw = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
    ctx.lineTo(x, y)
    ctx.stroke()
}
const startLine = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
    ctx.beginPath()
    ctx.moveTo(x, y)
}

const endLine = (ctx: CanvasRenderingContext2D) => {
    ctx.closePath()
    ctx.stroke()
    ctx.fill()
}

export interface DrawPoint {
    x: number
    y: number
    latitude: number
    longitude: number
}

interface UseDrawingOverlayParams {
    onChange?: (point: DrawPoint) => void
    onFinish?: (points: DrawPoint[] | undefined, allDrawings: DrawPoint[][]) => void
    active?: boolean
    options?: {
        strokeStyle?: string
        fillStyle?: string
        paddingX?: number
        paddingY?: number
        infiniteDraw?: boolean
    }
}

const useDrawingOverlay = (
    getTarget: () => HTMLElement | null,
    { onChange, onFinish, options, active: activeProp }: UseDrawingOverlayParams = {}
) => {
    const [active, setActive] = useState(activeProp ?? false)
    const { unproject } = useMapControl()

    const canvasRef = useRef(document.createElement("canvas"))
    const drawRef = useRef<DrawPoint[]>(undefined)
    const redoStackRef = useRef<DrawPoint[][]>([])
    const [drawResult, setDrawResult] = useState<DrawPoint[][]>()

    const canvasRectRef = useRef<DOMRect | null>(null)

    const stateRef = useRef({ drawResult, options, onChange, onFinish })
    stateRef.current = { drawResult, options, onChange, onFinish }

    const clearAndSetupCanvas = useCallback(
        (ctx: CanvasRenderingContext2D, width: number, height: number) => {
            const paddingX = stateRef.current.options?.paddingX ?? 0
            const paddingY = stateRef.current.options?.paddingY ?? 0

            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.clearRect(0, 0, width, height)
            ctx.translate(paddingX, paddingY)
        },
        []
    )

    const renderHistory = useCallback((ctx: CanvasRenderingContext2D) => {
        const history = stateRef.current.drawResult
        if (!history) return

        ctx.lineCap = "round"
        ctx.lineWidth = 2

        history.forEach((drawing) => {
            if (!drawing.length) return
            ctx.strokeStyle = stateRef.current.options?.strokeStyle ?? "#27BBFF"
            ctx.fillStyle = stateRef.current.options?.fillStyle ?? "#00000000"

            startLine(ctx, drawing[0].x, drawing[0].y)
            for (let i = 1; i < drawing.length; i++) {
                draw(ctx, drawing[i].x, drawing[i].y)
            }
            endLine(ctx)
        })
    }, [])

    useLayoutEffect(() => {
        if (!active) return

        const canvas = canvasRef.current
        const target = getTarget()
        const context = canvas.getContext("2d")
        if (!target || !context) return

        canvas.style.position = "fixed"
        canvas.style.pointerEvents = "auto"
        canvas.className = "cursor-crosshair"
        canvas.style.zIndex = `${+(getComputedStyle(target).zIndex || 0) + 1}`

        target.insertAdjacentElement("afterend", canvas)

        let animationFrameId: number
        let prevRectSignature = ""

        const syncLoop = () => {
            const rect = target.getBoundingClientRect()
            const paddingX = stateRef.current.options?.paddingX ?? 0
            const paddingY = stateRef.current.options?.paddingY ?? 0

            const rectSignature = `${rect.top},${rect.left},${rect.width},${rect.height},${paddingX},${paddingY}`

            if (rectSignature !== prevRectSignature) {
                canvas.style.top = `${rect.top - paddingY}px`
                canvas.style.left = `${rect.left - paddingX}px`

                const paddedWidth = rect.width + paddingX * 2
                const paddedHeight = rect.height + paddingY * 2

                canvas.style.width = `${paddedWidth}px`
                canvas.style.height = `${paddedHeight}px`

                if (canvas.width !== paddedWidth || canvas.height !== paddedHeight) {
                    canvas.width = paddedWidth
                    canvas.height = paddedHeight
                    clearAndSetupCanvas(context, paddedWidth, paddedHeight)
                    renderHistory(context)
                }

                canvasRectRef.current = canvas.getBoundingClientRect()
                prevRectSignature = rectSignature
            }
            animationFrameId = requestAnimationFrame(syncLoop)
        }
        syncLoop()

        const getPoint = (e: MouseEvent) => {
            const paddingX = stateRef.current.options?.paddingX ?? 0
            const paddingY = stateRef.current.options?.paddingY ?? 0

            const rect = canvasRectRef.current || canvas.getBoundingClientRect()

            const rawX = e.clientX - rect.left
            const rawY = e.clientY - rect.top

            const logicalX = rawX - paddingX
            const logicalY = rawY - paddingY

            const [lon, lat] = unproject([logicalX, logicalY])

            return { latitude: lat, longitude: lon, x: logicalX, y: logicalY }
        }

        const handleMouseMove = (e: MouseEvent) => {
            if (!drawRef.current) return

            const point = getPoint(e)
            draw(context, point.x, point.y)

            drawRef.current.push(point)
            stateRef.current.onChange?.(point)
        }

        const handleMouseUp = (e: MouseEvent) => {
            if (!drawRef.current) return

            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)

            canvas.removeEventListener("mousemove", handleMouseMove)
            canvas.removeEventListener("mouseup", handleMouseUp)
            canvas.removeEventListener("mouseleave", handleMouseUp)

            endLine(context)

            const point = getPoint(e)
            drawRef.current.push(point)

            const newDrawing = drawRef.current
            const currentHistory = stateRef.current.drawResult
            const newDrawResult = currentHistory ? [...currentHistory, newDrawing] : [newDrawing]

            setDrawResult(newDrawResult)
            redoStackRef.current = []
            stateRef.current.onFinish?.(newDrawing, newDrawResult)

            drawRef.current = undefined
        }

        const handleMouseDown = (e: MouseEvent) => {
            const { options, onChange } = stateRef.current
            const infinite = options?.infiniteDraw ?? false

            context.strokeStyle = options?.strokeStyle ?? "#27BBFF"
            context.fillStyle = options?.fillStyle ?? "#00000000"

            const point = getPoint(e)
            startLine(context, point.x, point.y)

            drawRef.current = [point]
            onChange?.(point)

            if (infinite) {
                window.addEventListener("mousemove", handleMouseMove)
                window.addEventListener("mouseup", handleMouseUp)
            } else {
                canvas.addEventListener("mousemove", handleMouseMove)
                canvas.addEventListener("mouseup", handleMouseUp)
                canvas.addEventListener("mouseleave", handleMouseUp)
            }
        }

        canvas.addEventListener("mousedown", handleMouseDown)

        return () => {
            cancelAnimationFrame(animationFrameId)
            canvas.removeEventListener("mousedown", handleMouseDown)

            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)
            canvas.remove()
        }
    }, [active, getTarget, unproject, renderHistory, clearAndSetupCanvas])

    useLayoutEffect(() => {
        if (active && canvasRef.current) {
            const ctx = canvasRef.current.getContext("2d")
            if (ctx) {
                clearAndSetupCanvas(ctx, canvasRef.current.width, canvasRef.current.height)
                renderHistory(ctx)
            }
        }
    }, [drawResult, active, renderHistory, clearAndSetupCanvas])

    const undo = () => {
        if (!drawResult?.length) return
        const newDrawings = [...drawResult]
        const lastDrawing = newDrawings.pop()
        if (lastDrawing) {
            redoStackRef.current.push(lastDrawing)
            setDrawResult(newDrawings)
            stateRef.current.onFinish?.(undefined, newDrawings)
        }
    }

    const redo = () => {
        if (!redoStackRef.current.length) return
        const drawingToRedo = redoStackRef.current.pop()
        if (drawingToRedo) {
            const newDrawings = [...(drawResult ?? []), drawingToRedo]
            setDrawResult(newDrawings)
            stateRef.current.onFinish?.(drawingToRedo, newDrawings)
        }
    }

    const clearDrawings = useCallback(() => {
        setDrawResult(undefined)
        stateRef.current.onFinish?.(undefined, [])
    }, [])

    return {
        active,
        setActive,
        drawings: drawResult,
        setDrawings: setDrawResult,
        clearDrawings,
        undo,
        redo,
    }
}

let isDrawing = false

interface UseMapDrawingOverlayParams {
    onChange?: (point: DrawPoint) => void
    onFinish?: (points: DrawPoint[], allDrawings: DrawPoint[][]) => void
}

export const activateMapDrawingOverlay = ({
    onFinish,
    id = "map",
    color,
}: UseMapDrawingOverlayParams & { id?: string; color?: string }) => {
    if (isDrawing) throw new Error("Drawing already initialized")
    color = color || hexColorSwatches[ColorSwatchCategory.FirePerimeterOutline][0]
    isDrawing = true
    const canvas = document.createElement("canvas")
    const drawings = [[]] as DrawPoint[][]

    const map = document.getElementById(id)!
    const context = canvas.getContext("2d")!

    canvas.className = "absolute w-full h-full z-10 pointer-events-none"

    canvas.width = map?.offsetWidth ?? 0
    canvas.height = map?.offsetHeight ?? 0
    context.lineWidth = 2
    context.lineCap = "round"
    context.strokeStyle = color
    canvas.id = "asd123"
    let isDragging = false

    const handleIsDraggingVertex = (_isDragging: boolean) => (isDragging = _isDragging)

    firePerimeterDragController.on("isDragging", handleIsDraggingVertex)

    map.insertAdjacentElement("beforebegin", canvas)

    let down = false
    const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement | null
        if (target?.className !== "maplibregl-canvas") return
        down = true
        startLine(context, e.offsetX, e.offsetY)
        const { longitude, latitude } = resolveEventLngLat(e)
        drawings.at(-1)!.push({ latitude, longitude, x: e.offsetX, y: e.offsetY })
        store.dispatch(disabledMapController())
    }
    const handleMouseMove = throttle((e: MouseEvent) => {
        if (isDragging) {
            drawings.at(-1)?.pop()
            down = false
            return
        }
        if (!down) return
        const { longitude, latitude } = resolveEventLngLat(e)
        const p2 = { latitude, longitude, x: e.offsetX, y: e.offsetY }
        const p1 = drawings.at(-1)!.at(-1)
        if (p1 && Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2) < 10) return
        draw(context, e.offsetX, e.offsetY)
        drawings.at(-1)?.push(p2)
    }, 100)
    const handleMouseUp = (e: MouseEvent) => {
        const target = e.target as HTMLElement | null
        if (!down || target?.className !== "maplibregl-canvas") return
        context?.clearRect(0, 0, canvas.width, canvas.height)
        down = false
        onFinish?.(drawings.at(-1)!, drawings)
        drawings.push([])
        store.dispatch(
            customMapController({ options: { dragRotate: false, doubleClickZoom: false } })
        )
    }

    map.addEventListener("mousedown", handleMouseDown)
    map.addEventListener("mousemove", handleMouseMove)
    map.addEventListener("mouseup", handleMouseUp)

    const cleanup = () => {
        isDrawing = false
        context?.clearRect(0, 0, canvas.width, canvas.height)
        canvas.remove()
        map.removeEventListener("mousedown", handleMouseDown)
        map.removeEventListener("mousemove", handleMouseMove)
        map.removeEventListener("mouseup", handleMouseUp)
        firePerimeterDragController.off("isDragging", handleIsDraggingVertex)
    }

    return { cleanup }
}

const useMapDrawingOverlay = (params: UseDrawingOverlayParams) => {
    const map = document.getElementById("map")

    return useDrawingOverlay(() => map, params)
}

export { useMapDrawingOverlay, useDrawingOverlay }
